import { cva, type VariantProps } from "class-variance-authority";

/**
 * Shared button class generator — single source of truth for the project's
 * button system. Lives in its own module (not in Button.tsx) so that both
 * <Button> and <IconButton> can consume it without tripping React Fast Refresh
 * (a component file must only export components).
 *
 * Variant ↔ class mapping (DeliveryPlatform button system, Sprint 106 decision 6):
 *   primary      → solid blue   (high emphasis, the main action)
 *   secondary    → outline      (medium emphasis, neutral action)
 *   ghost        → transparent  (low emphasis, inline / row actions)
 *   destructive  → solid red    (irreversible / danger action)
 *   danger       → alias of `destructive` (kept so callers can use either name)
 *
 * Sizes: sm / md / lg, plus `icon` / `icon-sm` (square, for icon-only buttons).
 *
 * Do NOT recreate this elsewhere. Icon-only actions MUST use <IconButton> so
 * they always get a tooltip + aria-label by construction.
 */
export const buttonVariants = cva(
  // `rounded-lg` (8px), not `rounded-md` (6px): the modal bodies already use 8–12px
  // radii on their own controls («Позвонить»/«Написать» are `rounded-xl`), so at 6px
  // the header buttons read as a different, older component set within the SAME
  // dialog. Measured before changing (2026-08-24): header 6px vs body 12px.
  "inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-brand-primary text-white hover:bg-brand-primary-hover focus-visible:ring-brand-primary",
        secondary:
          "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-400",
        ghost:
          "bg-transparent text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400",
        destructive:
          "bg-brand-danger text-white hover:bg-red-600 focus-visible:ring-brand-danger",
        // `danger` is an explicit alias of `destructive` (same red look) so both
        // names are accepted; admin/dealer migration code prefers `danger`.
        danger:
          "bg-brand-danger text-white hover:bg-red-600 focus-visible:ring-brand-danger",
        // TASK-4836 — the mockup's destructive action in a modal HEADER: outlined,
        // not filled. A solid red block sat beside two neutral outline buttons and
        // shouted; the outline keeps it unmistakably destructive (border + icon in
        // `brand-danger`) while matching the weight of «Редактировать» next to it.
        // Both remain available: filled `danger` stays correct for a confirm
        // dialog's final «Удалить», where shouting IS the point.
        "danger-outline":
          "border border-brand-danger/40 bg-white text-brand-danger hover:bg-brand-danger/10 focus-visible:ring-brand-danger",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4",
        lg: "h-12 px-6 text-base",
        // Square sizes for icon-only buttons (no horizontal padding).
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        // TASK-4836 — the mockup's ACCENT icon button (the red trash in a modal
        // header). Deliberately a step larger than `icon-sm` and rounder than the
        // base `rounded-lg`: at 32×32/8px it rendered identically to the ghost
        // icons beside it, so a destructive action carried exactly the same visual
        // weight as «Закрыть». Emphasis is the whole point of the filled variant.
        "icon-md": "h-10 w-10 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>;
export type ButtonSize = NonNullable<
  VariantProps<typeof buttonVariants>["size"]
>;
